"use client";
import { useAccordion } from "@/components/useAccordion";
import { Checkbox, Link } from "@cappelendamm/design-system/react";
import classNames from "classnames";
import { PiMinus, PiPlus } from "react-icons/pi";
import { useItemListing } from "./ItemListingContext";
import { startTransition, useMemo, useOptimistic, useState } from "react";
import { useSearchParams } from "next/navigation";
import { FacetConfig } from "../constants";
import { useRefineFacet } from "../hooks/useRefineFacet";

type FacetItemType = {
  label: string;
  value: string;
  count: number;
  isRefined: boolean;
};

type FacetItemProps = {
  item: FacetItemType;
  refine: (value: string) => void;
};

function FacetItem({ item, refine }: FacetItemProps) {
  const [optimisticChecked, addOptimisticUpdate] = useOptimistic(
    item.isRefined,
  );

  return (
    <li key={item.label}>
      <label className="grid h-12 grid-cols-[auto_1fr_auto] items-center gap-x-4">
        <Checkbox
          checked={optimisticChecked}
          onChange={() => {
            startTransition(() => {
              addOptimisticUpdate(!item.isRefined);
              refine(item.value);
            });
          }}
        />
        <span
          className="overflow-hidden text-ellipsis whitespace-nowrap"
          title={item.label}
        >
          {item.label}
        </span>
        <span className="text-right text-sm">{`${item.count}`}</span>
      </label>
    </li>
  );
}

export type FacetProps = {
  config: FacetConfig;
  initialExpanded?: boolean;
  searchable?: boolean;
  limit?: number;
  operation?: "or" | "and";
};

export function Facet({
  config: { attribute, title, queryParam = attribute },
  searchable = false,
  initialExpanded,
  limit = 4,
}: FacetProps) {
  const searchParams = useSearchParams();
  const { facets, filteredFacets, filterFacet } = useItemListing();

  const refine = useRefineFacet({ queryParam });

  const facetItems = filteredFacets[attribute] ?? facets[attribute] ?? [];

  const canToggleShowMore = facetItems.length > limit;
  const [isLimited, setIsLimited] = useState(true);

  const values = searchParams.getAll(queryParam);

  const items = useMemo(() => {
    return facetItems.map(({ value, count }) => ({
      label: value,
      value,
      count,
      isRefined: values.includes(value),
    }));
  }, [facetItems, attribute, values]);

  const {
    isExpanded,
    aria: { button, panel },
    toggle,
  } = useAccordion(initialExpanded ?? items.length > 1);

  if ((facets[attribute]?.length ?? 0) === 0) return null;

  return (
    <fieldset className="block h-fit">
      <div
        className={classNames(
          "grid grid-cols-[1fr_auto] items-center border-b",
          { "pb-4": isExpanded },
        )}
      >
        <legend onClick={toggle} className={"font-semibold"}>
          {title}
        </legend>
        <button
          className="flex h-12 w-12 items-center justify-end"
          onClick={toggle}
          {...button}
        >
          {isExpanded ? <PiMinus /> : <PiPlus />}
          <span className="sr-only">
            {isExpanded ? `Vis ${title}` : `Gjem ${title}`}
          </span>
        </button>
        {isExpanded ? (
          <div
            className={classNames(
              isExpanded ? "grid" : "hidden",
              "col-span-full",
            )}
            {...panel}
          >
            {searchable ? (
              <>
                <label
                  htmlFor={`${attribute}-search`}
                  className="sr-only"
                >{`Søk ${title}`}</label>
                <input
                  className="cvr-control col-span-full rounded-full px-4"
                  id={`${attribute}-search`}
                  type="search"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  maxLength={512}
                  onChange={async (event) => {
                    await filterFacet(attribute, event.currentTarget.value);
                  }}
                />
              </>
            ) : null}
            <ul className="col-span-full">
              {items.slice(0, isLimited ? limit : undefined).map((item) => (
                <FacetItem key={item.label} item={item} refine={refine} />
              ))}
            </ul>
            {canToggleShowMore ? (
              <Link
                as="button"
                onClick={() => setIsLimited((prev) => !prev)}
                className={"justify-start"}
              >
                {!isLimited ? "Vis færre" : "Vis flere"}
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
